import React from 'react';

import * as Yup from 'yup';

import ItemModalProvider, {
	ItemModalProviderContext,
} from '@common/react/components/Core/ItemModalProvider/ItemModalProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { simpleStringValidator } from '@common/react/utils/validationHelpers';
import { useModal } from '@common/react/components/Modal/ModalContextProvider';
import { ItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';

import { Feature } from '@commonTuna/react/objects/CompanyFeature';

import { AppointmentFile } from '@app/objects/AppointmentFile';
import { AppointmentEmail } from '@app/objects/AppointmentEmail';
import { useCompanyFeatureProviderContext } from '@app/components/UI/CompanyFeaturesSetter/CompanyFeaturesSetter';
import SendEmailReadonly, { messageKey } from '@app/components/UI/SendEmailModal/SendEmailReadonly';
import SendEmailEdit from '@app/components/UI/SendEmailModal/SendEmailEdit';

/*
export interface EmailFormValues {
	id: number;
	to: Array<string>;
	cc: Array<string>;
	bcc: Array<string>;
	subject: string;
	html: string;
	attachments: Array<AppointmentFile>;
	patientId: number;
	inquiryId?: number;
	portalId?: number;
	archive: boolean;
	read?: boolean;
	useCustomMailboxConfig?: boolean;
}
*/

interface SendEmailModalProps {
	children?: React.ReactNode | ((
		context: ItemProviderContext<AppointmentEmail>,
		modalContext: ItemModalProviderContext<AppointmentEmail>,
	) => React.ReactNode);
	initialEmail?: string;
	initialPatientId?: number;
	button?: (onClick: () => void, disabled: boolean, title: string) => JSX.Element;
	inquiryId?: number;
	portalId?: number;
	viewMode?: boolean;
	appointmentEmail?: AppointmentEmail;
	afterSubmit?: (appointmentEmail: AppointmentEmail) => void;
	onClose?: () => void;
	defaultVisible?: boolean;
	patientName?: string;
	onCloseAfterChange?: () => void;
	readonly?: boolean;
	onSendError?: (err: string) => void;
	readonlyTo?: boolean;
	initialAttachments?: Array<AppointmentFile>;
}

const SendEmailModal: React.FC<SendEmailModalProps> = (props) => {
	const {
		children,
		onCloseAfterChange,
		onClose,
		button,
		initialPatientId,
		inquiryId,
		portalId,
		initialEmail,
		onSendError,
		initialAttachments,
		patientName,
		readonlyTo = false,
		readonly,
		...rest
	} = props;
	const { openSuccessMessage, openErrorMessage } = useModal();
	const { checkFeatureAccess } = useCompanyFeatureProviderContext();
	const emailFeatureIsEnabled = checkFeatureAccess(Feature.Email);
	const [changed, setChanged] = React.useState(false);

	const handleClose = () => {
		onClose && onClose();
		if (changed) {
			onCloseAfterChange && onCloseAfterChange();
		}
		setChanged(false);
	};

	return <ItemModalProvider<AppointmentEmail>
		getModalProps={(item) => {
			return {
				width: 900,
				afterClose: handleClose,
				styles: { mask: { zIndex: 1001 } },
				className: 'send-email-modal',
				wrapClassName: `send-email-modal__wrap ${item.id < 0 ? '' : 'send-email-modal__wrap__readonly'}`,
				header: null,
				footer: null,
			};
		}}
		onCloseAfterSave={onCloseAfterChange}
		closeAfterSave={!onCloseAfterChange}
		validationSchema={Yup.object().shape({
			to: Yup.array().test('Required field!', 'Invalid Email', (value) => {
				if (!value || value?.length === 0) return false;
				return value?.filter((email) => {
					try {
						Yup.string().email().validateSync(email);
						return true;
					} catch (e) {
						return false;
					}
				}).length === value.length;
			}),
			subject: simpleStringValidator,
			html: simpleStringValidator,
		})}
		loadBeforeOpen
		saveRequest="sendReply"
		clearForSubmit={({ item }) => ({
			to: Array.isArray(item.to) ? item.to.join(',') : item.to,
			cc: Array.isArray(item.cc) ? item.cc.join(',') : item.cc,
			bcc: Array.isArray(item.bcc) ? item.bcc.join(',') : item.bcc,
			attachments: item.id > 0 ? undefined : item.attachments,
			patient: undefined,
			initialAttachmentIds: initialAttachments?.map((attachment) => attachment.id),
		} as any)}
		loadRequest="getAppointmentEmail"
		render={(item, setOpen) => <ItemEditor<AppointmentEmail>
			readonly={item.id > 0}
			getInitialValues={(values) => (values)}
			afterSubmit={({ item }) => {
				if (!(item.id > 0)) setOpen(false);

				openSuccessMessage?.(readonly ? 'Saved' : 'Email was sent', { key: messageKey });
			}}
			formikProps={{
				enableReinitialize: true,
			}}
			edit={({ state: { loading, error }, formikBag }) => <SendEmailEdit
				item={item}
				form={formikBag}
				loading={loading}
				error={error}
				withAttachmentButton={!initialAttachments || initialAttachments.length === 0}
				readonlyTo={readonlyTo}
			/>}
			view={({ state: { item }, actions: { setItem } }) => <SendEmailReadonly
				item={item}
				patientName={patientName}
				setOpen={setOpen}
				setItem={setItem}
				setChanged={setChanged}
			/>}
		/>}
		onSaveRequestError={onSendError}
		onRequestError={(err) => openErrorMessage?.(err)}
		buttonTitle="Show"
	>
		{(modalContext, context) => <>
			{typeof children === 'function' ? children(context, modalContext) : children}
			{button && button(
				() => modalContext.actions.openModal({
					id: -1,
					to: initialEmail ? [initialEmail] : [] as Array<string>,
					cc: [] as Array<string>,
					bcc: [] as Array<string>,
					subject: '',
					html: '',
					attachments: initialAttachments ?? [],
					patientId: initialPatientId ?? -1,
					inquiryId,
					portalId,
					archive: false,
					useCustomMailboxConfig: false,
				} as AppointmentEmail),
				!emailFeatureIsEnabled,
				emailFeatureIsEnabled ? '' : 'Email feature is disabled',
			)}
		</>}
	</ItemModalProvider>;
};

export default SendEmailModal;
